import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { PortalNavigationProvider } from "@/contexts/PortalNavigationContext";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import DivisionsPage from "@/pages/DivisionsPage";
import CertificationPage from "@/pages/CertificationPage";
import ExaminationPathwaysPage from "@/pages/ExaminationPathwaysPage";
import ApplyPage from "@/pages/ApplyPage";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import LogbookPage from "@/pages/LogbookPage";
import OSCEPage from "@/pages/OSCEPage";
import MCQExamPage from "@/pages/MCQExamPage";
import VerificationPage from "@/pages/VerificationPage";
import ContactPage from "@/pages/ContactPage";
import EthicsPage from "@/pages/EthicsPage";
import AdvisoryBoardPage from "@/pages/AdvisoryBoardPage";
import PoliciesPage from "@/pages/PoliciesPage";
import AccreditationPage from "@/pages/AccreditationPage";
import CurriculumPage from "@/pages/CurriculumPage";
import AdminPage from "@/pages/AdminPage";
import FacultyPage from "@/pages/FacultyPage";
import AestheticMedicinePage from "@/pages/AestheticMedicinePage";
import LongevityMedicinePage from "@/pages/LongevityMedicinePage";
import RegenerativeMedicinePage from "@/pages/RegenerativeMedicinePage";
import IPCFPage from "@/pages/IPCFPage";
import GovernancePage from "@/pages/GovernancePage";
import InstitutionalApplyPage from "@/pages/InstitutionalApplyPage";
import CurriculumApplyPage from "@/pages/CurriculumApplyPage";
import ExamCertDivisionPage from "@/pages/ExamCertDivisionPage";
import AlliedFacultyPage from "@/pages/AlliedFacultyPage";
import DentalFacultyPage from "@/pages/DentalFacultyPage";
import AestheticDentistryPage from "@/pages/AestheticDentistryPage";
import AestheticNursingPage from "@/pages/AestheticNursingPage";
import ClinicalAssistancePage from "@/pages/ClinicalAssistancePage";
import ExaminationCentersPage from "@/pages/ExaminationCentersPage";
import ExaminationCenterApplyPage from "@/pages/ExaminationCenterApplyPage";
import InstitutionalLoginPage from "@/pages/InstitutionalLoginPage";
import InstitutionalDashboard from "@/pages/InstitutionalDashboard";
import CurriculumLoginPage from "@/pages/CurriculumLoginPage";
import CurriculumDashboard from "@/pages/CurriculumDashboard";
import AccreditationDashboard from "@/pages/AccreditationDashboard";
import ExaminerDashboard from "@/pages/ExaminerDashboard";
import ExaminerLoginPage from "@/pages/ExaminerLoginPage";
import ExaminerApplyPage from "@/pages/ExaminerApplyPage";
import PortalSelectionPage from "@/pages/PortalSelectionPage";
import ExaminationCenterLoginPage from "@/pages/ExaminationCenterLoginPage";
import ExaminationCenterDashboard from "@/pages/ExaminationCenterDashboard";
import CaseLogPage from "@/pages/CaseLogPage";
import TranscriptPage from "@/pages/TranscriptPage";
import CandidateAssessmentPage from "@/pages/CandidateAssessmentPage";
import ExaminationStructurePage from "@/pages/ExaminationStructurePage";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import EventsGalleryPage from "@/pages/EventsGalleryPage";
import GASIPage from "@/pages/GASIPage";
import CPMPage from "@/pages/CPMPage";
import NewsMediaPage from "@/pages/NewsMediaPage";
import CBELMMPage from "@/pages/CBELMMPage";
import GovernanceStructurePage from "@/pages/GovernanceStructurePage";
import ExaminationFrameworkPage from "@/pages/ExaminationFrameworkPage";
import CandidateHandbookPage from "@/pages/CandidateHandbookPage";
import IndependencePolicyPage from "@/pages/IndependencePolicyPage";
import CPDRecognitionOverviewPage from "@/pages/CPDRecognitionOverviewPage";
import CPDRecognitionApplyPage from "@/pages/CPDRecognitionApplyPage";
import CPDRecognitionStandardsPage from "@/pages/CPDRecognitionStandardsPage";
import CPDRecognizedActivitiesPage from "@/pages/CPDRecognizedActivitiesPage";
import CPDRecognitionFeesPage from "@/pages/CPDRecognitionFeesPage";
import CPDRecognitionFAQPage from "@/pages/CPDRecognitionFAQPage";

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  return children;
}

function AppLayout() {
  return (
    <BrowserRouter>
      <NotificationProvider>
        <PortalNavigationProvider>
        <div className="flex flex-col min-h-screen bg-white">
          <Header />
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/divisions" element={<DivisionsPage />} />
              <Route path="/certification" element={<CertificationPage />} />
              <Route path="/examination-pathways" element={<ExaminationPathwaysPage />} />
            <Route path="/apply" element={<ProtectedRoute><ApplyPage /></ProtectedRoute>} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/portals" element={<PortalSelectionPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/logbook" element={<ProtectedRoute><LogbookPage /></ProtectedRoute>} />
            <Route path="/osce" element={<OSCEPage />} />
            <Route path="/mcq" element={<ProtectedRoute><MCQExamPage /></ProtectedRoute>} />
            <Route path="/examination-structure" element={<ExaminationStructurePage />} />
            <Route path="/verification" element={<VerificationPage />} />
            <Route path="/verify" element={<Navigate to="/verification" replace />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/ethics" element={<EthicsPage />} />
            <Route path="/advisory-board" element={<AdvisoryBoardPage />} />
            <Route path="/policies" element={<PoliciesPage />} />
            <Route path="/accreditation" element={<AccreditationPage />} />
            <Route path="/curriculum" element={<CurriculumPage />} />
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            <Route path="/examiner" element={<ProtectedRoute><ExaminerDashboard /></ProtectedRoute>} />
            <Route path="/faculty" element={<FacultyPage />} />
            <Route path="/faculty/aesthetic-medicine" element={<AestheticMedicinePage />} />
            <Route path="/faculty/longevity-medicine" element={<LongevityMedicinePage />} />
            <Route path="/faculty/regenerative-medicine" element={<RegenerativeMedicinePage />} />
            <Route path="/ipcf" element={<IPCFPage />} />
            <Route path="/governance" element={<GovernancePage />} />
            <Route path="/division-examination-certification" element={<ExamCertDivisionPage />} />
            <Route path="/apply/institutional" element={<ProtectedRoute><InstitutionalApplyPage /></ProtectedRoute>} />
            <Route path="/apply/examination-center" element={<ProtectedRoute><ExaminationCenterApplyPage /></ProtectedRoute>} />
            <Route path="/apply/curriculum" element={<ProtectedRoute><CurriculumApplyPage /></ProtectedRoute>} />
            <Route path="/allied-faculty" element={<AlliedFacultyPage />} />
            <Route path="/dental-faculty" element={<DentalFacultyPage />} />
            <Route path="/dental-faculty/aesthetic-dentistry" element={<AestheticDentistryPage />} />
            <Route path="/allied-faculty/aesthetic-nursing" element={<AestheticNursingPage />} />
            <Route path="/allied-faculty/clinical-assistance" element={<ClinicalAssistancePage />} />
            <Route path="/examination-centers" element={<ExaminationCentersPage />} />
            <Route path="/login/institutional" element={<InstitutionalLoginPage />} />
            <Route path="/login/curriculum" element={<CurriculumLoginPage />} />
            <Route path="/dashboard/institutional" element={<ProtectedRoute><InstitutionalDashboard /></ProtectedRoute>} />
            <Route path="/dashboard/accreditation" element={<ProtectedRoute><AccreditationDashboard /></ProtectedRoute>} />
            <Route path="/dashboard/curriculum" element={<ProtectedRoute><CurriculumDashboard /></ProtectedRoute>} />
            <Route path="/login/examination-center" element={<ExaminationCenterLoginPage />} />
            <Route path="/login/examiner" element={<ExaminerLoginPage />} />
            <Route path="/apply/examiner" element={<ExaminerApplyPage />} />
            <Route path="/dashboard/examination-center" element={<ProtectedRoute><ExaminationCenterDashboard /></ProtectedRoute>} />
            <Route path="/case-logs" element={<ProtectedRoute><CaseLogPage /></ProtectedRoute>} />
            <Route path="/transcript" element={<ProtectedRoute><TranscriptPage /></ProtectedRoute>} />
            <Route path="/examiner/assess/:candidateId" element={<ProtectedRoute><CandidateAssessmentPage /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
            <Route path="/events-gallery" element={<EventsGalleryPage />} />
            <Route path="/gasi" element={<GASIPage />} />
            <Route path="/cpm" element={<CPMPage />} />
            <Route path="/news-media" element={<NewsMediaPage />} />
            <Route path="/examination/cbe-lmm" element={<CBELMMPage />} />
            <Route path="/governance-structure" element={<GovernanceStructurePage />} />
            <Route path="/examination-framework" element={<ExaminationFrameworkPage />} />
            <Route path="/candidate-handbook" element={<CandidateHandbookPage />} />
            <Route path="/independence-policy" element={<IndependencePolicyPage />} />
            <Route path="/cpd-recognition" element={<CPDRecognitionOverviewPage />} />
            <Route path="/cpd-recognition/apply" element={<CPDRecognitionApplyPage />} />
            <Route path="/cpd-recognition/standards" element={<CPDRecognitionStandardsPage />} />
            <Route path="/cpd-recognition/recognized-activities" element={<CPDRecognizedActivitiesPage />} />
            <Route path="/cpd-recognition/fees" element={<CPDRecognitionFeesPage />} />
            <Route path="/cpd-recognition/faq" element={<CPDRecognitionFAQPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
      <Toaster position="top-right" />
      </PortalNavigationProvider>
      </NotificationProvider>
    </BrowserRouter>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppLayout />
    </AuthProvider>
  );
}

export default App;
